import { useLingui } from '@lingui/react/macro';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import { Box, type SxProps, type Theme, Tooltip } from '@mui/material';
import type { MarketBoost } from '@/common/boostedMarkets';

const boostChipStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 16,
  px: 0.375,
  color: 'warning.light',
  border: '1px solid currentColor',
  borderRadius: 'var(--mui-shape-borderRadius)',
  cursor: 'help',
  typography: 'body2',
} as const;

const boostLabelChipStyles = {
  ...boostChipStyles,
  height: 24,
  px: 0.75,
  cursor: 'default',
} as const;

// Same chip, but it carries a description tooltip, so it keeps the help affordance.
const boostLabelChipWithTooltipStyles = {
  ...boostLabelChipStyles,
  cursor: 'help',
} as const;

const boostChipLabelStyles = {
  px: 0.25,
} as const;

const boostIconStyles = {
  display: 'block',
  color: 'warning.light',
  cursor: 'help',
} as const;

function BoostChipBase({ label, sx, tooltip }: { label: string; sx: SxProps<Theme>; tooltip?: string }) {
  const chip = (
    <Box sx={sx}>
      <WhatshotRoundedIcon fontSize="xSmall" />
      <Box component="span" sx={boostChipLabelStyles}>
        {label}
      </Box>
    </Box>
  );

  if (tooltip == null) {
    return chip;
  }

  return (
    <Tooltip title={tooltip} arrow placement="top">
      {chip}
    </Tooltip>
  );
}

// Compact surfaces show the short label (or a generic "Boost"); the tooltip carries the
// description, falling back to the full label when none is configured.
export function BoostChip({ boost }: { boost: MarketBoost }) {
  const { t } = useLingui();

  return (
    <BoostChipBase
      sx={boostChipStyles}
      tooltip={boost.description ?? boost.full}
      label={boost.short ?? t({ comment: 'Boosted market chip label', message: 'Boost' })}
    />
  );
}

export function BoostIcon({ boost }: { boost: MarketBoost }) {
  return (
    <Tooltip title={boost.description ?? boost.full} arrow placement="top">
      <WhatshotRoundedIcon fontSize="small" sx={boostIconStyles} />
    </Tooltip>
  );
}

// Wide surfaces (market stats panel) have room for the full label, so the tooltip is used
// only for the description — the chip has none when the config omits it.
export function BoostLabelChip({ boost }: { boost: MarketBoost }) {
  return (
    <BoostChipBase
      sx={boost.description ? boostLabelChipWithTooltipStyles : boostLabelChipStyles}
      tooltip={boost.description}
      label={boost.full}
    />
  );
}
